/**
 * Agent Management Service — wraps /api/admin/agents/ + /api/admin/skills/
 * Phase 4.7 — WQ-164
 */

// === Types ===

export interface Agent {
  id: string;
  name: string;
  agent_type: string;
  description: string | null;
  status: string;
  capabilities: Record<string, unknown> | null;
  constraints: Record<string, unknown> | null;
  is_essential: boolean;
  tier: string | null;
  skill_binding: Record<string, unknown> | null;
  ml_permissions: Record<string, unknown> | null;
  tenant_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface TierMatrix {
  matrix: Record<string, Record<string, boolean>>;
  description: string;
}

export interface MLActions {
  actions: string[];
  count: number;
}

export interface ValidationResult {
  allowed: boolean;
  reason: string;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// === API Functions ===

export async function listAgents(
  authFetch: FetchFn,
  filters?: { tier?: string; status?: string; tenant_id?: string }
): Promise<Agent[]> {
  const params = new URLSearchParams();
  if (filters?.tier) params.set('tier', filters.tier);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.tenant_id) params.set('tenant_id', filters.tenant_id);
  const qs = params.toString();
  const res = await authFetch(`/api/admin/agents/${qs ? '?' + qs : ''}`);
  if (!res.ok) throw new Error(`Failed to list agents: ${res.status}`);
  return res.json();
}

export async function getAgent(authFetch: FetchFn, id: string): Promise<Agent> {
  const res = await authFetch(`/api/admin/agents/${id}`);
  if (!res.ok) throw new Error(`Failed to get agent: ${res.status}`);
  return res.json();
}

export async function updateAgent(
  authFetch: FetchFn,
  id: string,
  data: Partial<Agent>
): Promise<Agent> {
  const res = await authFetch(`/api/admin/agents/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to update agent: ${res.status}`);
  return res.json();
}

export async function getTierMatrix(authFetch: FetchFn): Promise<TierMatrix> {
  const res = await authFetch('/api/admin/agents/tiers');
  if (!res.ok) throw new Error(`Failed to get tier matrix: ${res.status}`);
  return res.json();
}

export async function getMLActions(authFetch: FetchFn): Promise<MLActions> {
  const res = await authFetch('/api/admin/agents/ml-actions');
  if (!res.ok) throw new Error(`Failed to get ML actions: ${res.status}`);
  return res.json();
}

export async function validateAction(
  authFetch: FetchFn,
  agentId: string,
  action: string
): Promise<ValidationResult> {
  const res = await authFetch(`/api/admin/agents/${agentId}/validate-action`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action }),
  });
  if (!res.ok) throw new Error(`Failed to validate action: ${res.status}`);
  return res.json();
}
